import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Package2, 
  X, 
  Upload, 
  Save, 
  ArrowLeft, 
  BarChart3,
  BookOpen,
  History,
  FolderOpen,
  MessageCircle,
  Users,
  Link as LinkIcon,
  AlertCircle,
  User,
  Sparkles,
  Shield,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Sidebar from '../components/layout/Sidebar';
import { useLocation } from 'react-router-dom';
import { stockMenuItems } from './StokFlow'; // Import stockMenuItems from StokFlow
import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

// Initialize Supabase clients
const supabaseUrl = import.meta.env.VITE_FILE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_FILE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Initialize User Profiles Supabase client (different database)
const userProfilesUrl = import.meta.env.VITE_SUPABASE_URL;
const userProfilesKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const userProfilesSupabase = createClient(userProfilesUrl, userProfilesKey);

const StokProductCreate = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarUserProfile, setSidebarUserProfile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [infoBoxOpen, setInfoBoxOpen] = useState(false);
  const [redirectProgress, setRedirectProgress] = useState(100);
  const redirectTimerRef = useRef(null);
  
  // Bulk creation states
  const [isBulkCreating, setIsBulkCreating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [createdCount, setCreatedCount] = useState(0);
  const [showBulkProgress, setShowBulkProgress] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState(false);

  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  // Add permissions state
  const [permissions, setPermissions] = useState({
    stok_add_permission: false,
    stok_edit_permission: false,
    stok_movement_permission: false,
    stok_delete_permission: false
  });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const [productData, setProductData] = useState({
    name: '',
    description: '',
    stock: '',
    location: '',
    purchaseLink: '',
    image: null,
    imageUrl: '',
    created_by: ''
  });

  const [imageSource, setImageSource] = useState('file');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProductData(prev => ({
        ...prev,
        image: e.target.files[0],
        imageUrl: '' // Clear URL when file is selected
      }));
      setImageSource('file');
    }
  };

  const handleImageUrlChange = (e) => {
    const { value } = e.target;
    setProductData(prev => ({
      ...prev,
      imageUrl: value,
      image: null // Clear file when URL is entered
    }));
    setImageSource('url');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setProductData(prev => ({
        ...prev,
        image: e.dataTransfer.files[0],
        imageUrl: ''
      }));
      setImageSource('file');
    }
  };

  const handleDropzoneClick = () => {
    fileInputRef.current.click();
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    
    // If empty, allow it
    if (value === '') {
      setProductData(prev => ({
        ...prev,
        location: ''
      }));
      return;
    }
    
    // Check first character - must be a letter
    if (value.length >= 1) {
      const firstChar = value.charAt(0);
      if (!/[a-zA-Z]/.test(firstChar)) {
        return; // Reject input if first character isn't a letter
      }
    }
    
    let formattedValue = '';
    
    // Process the input based on its length
    if (value.length === 1) {
      // First character should be uppercase letter
      formattedValue = value.toUpperCase() + '-';
    } else if (value.length === 2) {
      // Second character should be a hyphen
      if (value.charAt(1) !== '-') {
        // If user typed something other than hyphen, replace it with hyphen
        formattedValue = value.charAt(0).toUpperCase() + '-';
      } else {
        formattedValue = value.charAt(0).toUpperCase() + '-';
      }
    } else if (value.length >= 3 && value.length <= 4) {
      // Characters 3-4 should be digits
      const prefix = value.substring(0, 2); // A-
      const digits = value.substring(2);
      
      // Only allow digits after the hyphen
      if (!/^\d*$/.test(digits)) {
        // If input contains non-digits after hyphen, reject it
        return;
      }
      
      formattedValue = prefix + digits;
      
      // Automatically add underscore after two digits
      if (digits.length === 2) {
        formattedValue += '_';
      }
    } else if (value.length >= 5) {
      // Get the parts
      const prefix = value.substring(0, 2); // A-
      const digits = value.substring(2, 4);
      const underscore = value.substring(4, 5);
      const lastChar = value.substring(5, 6);
      
      // Validate underscore
      if (underscore !== '_') {
        // If 5th character isn't underscore, make it one
        formattedValue = prefix + digits + '_';
      } else {
        formattedValue = prefix + digits + '_';
        
        // Last character must be 1, 2, or 3
        if (lastChar && !['1', '2', '3'].includes(lastChar)) {
          // If last character isn't valid, reject it
          return;
        }
        
        if (lastChar) {
          formattedValue += lastChar;
        }
      }
    }
    
    setProductData(prev => ({
      ...prev,
      location: formattedValue
    }));
  };

  // Function to upload image to Supabase Storage
  const uploadImage = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `photos/${fileName}`; // photos klasörüne yükle
  
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
  
      if (error) throw error;
  
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);
  
      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };
  

  const validateForm = () => {
    if (!productData.name.trim()) {
      setError('Ürün adı girilmelidir');
      return false;
    }
    
    /* 
    if (!productData.stock || productData.stock < 0) {
      setError('Geçerli bir stok miktarı girilmelidir');
      return false;
    }
    */

    if (!productData.location || productData.location.length !== 6) {
      setError('Geçerli bir dolap konumu girilmelidir Örn: (A-12_1 formatında)');
      return false;
    }
    
    return true;
  };

  // Check if product name or location already exists
  const checkDuplicates = async () => {
    try {
      // Check for duplicate product name
      const { data: nameData, error: nameError } = await supabase
        .from('products')
        .select('id')
        .eq('name', productData.name.trim())
        .limit(1);
      
      if (nameError) throw nameError;
      
      if (nameData && nameData.length > 0) {
        setError(`"${productData.name}" adında bir ürün zaten mevcut. Lütfen farklı bir isim seçin.`);
        return false;
      }
      
      // Check for duplicate location
      const { data: locationData, error: locationError } = await supabase
        .from('products')
        .select('id, name')
        .eq('location', productData.location)
        .limit(1);
      
      if (locationError) throw locationError;
      
      if (locationData && locationData.length > 0) {
        setError(`"${productData.location}" konumu zaten "${locationData[0].name}" ürünü tarafından kullanılıyor. Lütfen farklı bir konum seçin.`);
        return false;
      }
      
      // No duplicates found
      return true;
    } catch (error) {
      console.error('Duplicate check error:', error);
      setError('Mevcut ürünler kontrol edilirken bir hata oluştu.');
      return false;
    }
  };

  // Callback function to receive userProfile from Sidebar
  const handleUserProfile = useCallback((profile) => {
    setSidebarUserProfile(profile);
  }, []); // Empty dependency array since we only need to set the profile once

  // Add cleanup function for timer
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearInterval(redirectTimerRef.current);
      }
    };
  }, []);

  const startRedirectTimer = () => {
    const startTime = Date.now();
    const duration = 5000; // 5 seconds

    redirectTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, duration - elapsed);
      const progress = (remaining / duration) * 100;
      
      setRedirectProgress(progress);
      
      if (progress === 0) {
        clearInterval(redirectTimerRef.current);
        navigate('/stokflow/products');
      }
    }, 50); // Update every 50ms for smooth animation
  };

  const cancelRedirect = () => {
    if (redirectTimerRef.current) {
      clearInterval(redirectTimerRef.current);
      setSuccess(false);
      setRedirectProgress(100);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset states
    setError(null);
    setSuccess(false);
    
    // Validate form data
    if (!validateForm()) {
      return;
    }
    
    // Check for duplicates before proceeding
    const isDuplicatesFree = await checkDuplicates();
    if (!isDuplicatesFree) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Handle image upload if needed
      let finalImageUrl = productData.imageUrl;
      
      if (productData.image) {
        try {
          finalImageUrl = await uploadImage(productData.image);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          setError('Görsel yüklenemedi, ancak ürün bilgileri kaydedilecek.');
        }
      }

      // Use userProfile from Sidebar
      let creatorName = 'Belirtilmemiş Kullanıcı';
      if (sidebarUserProfile) {
        creatorName = `${sidebarUserProfile.first_name} ${sidebarUserProfile.last_name}`;
      }
      
      // Create product in database
      const { data: newProduct, error: dbError } = await supabase
        .from('products')
        .insert([
          {
            name: productData.name,
            description: productData.description || null,
            stock: parseInt(productData.stock) || 0,
            location: productData.location,
            purchase_link: productData.purchaseLink || null,
            image_url: finalImageUrl || null,
            created_at: new Date().toISOString(),
            created_by: creatorName,
            stock_history: [
              {
                type: 'create',
                amount: parseInt(productData.stock) || 0,
                previous_stock: 0,
                new_stock: parseInt(productData.stock) || 0,
                updated_at: new Date().toISOString(),
                updated_by: creatorName,
                note: 'İlk ürün kaydı'
              }
            ]
          }
        ])
        .select();
      
      if (dbError) throw dbError;
      
      // Clear the form after successful submission
      setProductData({
        name: '',
        description: '',
        stock: '',
        location: '',
        purchaseLink: '',
        image: null,
        imageUrl: '',
        created_by: ''
      });

      // Reset the image source to file
      setImageSource('file');
      
      // Show success message and start redirect timer
      setSuccess(true);
      startRedirectTimer();
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Bir hata oluştu: ' + (err.message || 'Lütfen tekrar deneyin'));
    } finally {
      setLoading(false);
    }
  };

  // Arrays for random product generation
  const productPrefixes = [
    'Çelik', 'Aluminyum', 'Plastik', 'Metal', 'Ahşap', 'Elektronik', 'Silikon', 'Kauçuk',
    'Fiber', 'Karbon', 'Bakır', 'Çinko', 'Titanyum', 'Paslanmaz', 'Döküm', 'Nikel',
    'Cam', 'Seramik', 'PVC', 'Bronz', 'Pirinç', 'Kompozit', 'Krom', 'Polimer'
  ];
  
  const productTypes = [
    'Vida', 'Somun', 'Civata', 'Pim', 'Bağlantı', 'Kablo', 'Konnektör', 'Sensör',
    'Motor', 'Dişli', 'Rulman', 'Kılavuz', 'Yatağı', 'Kaplin', 'Conta', 'Keçe',
    'Boru', 'Hortum', 'Valf', 'Adaptör', 'Bağlantı Parçası', 'Kelepçe', 'Menteşe',
    'Kilit', 'Anahtar', 'Termostat', 'Rezistans', 'İzolatör', 'Transformatör',
    'Sigorta', 'Şalter', 'Panel', 'Kontrolör', 'Röle', 'Anahtar', 'Baskı Plakası',
    'Mil', 'Şaft', 'Devre', 'Modül', 'Aks', 'Eksen', 'Piston', 'Filtre', 'Enjektör'
  ];
  
  const productSuffixes = [
    'Başlığı', 'Grubu', 'Seti', 'Ünitesi', 'Kiti', 'Mekanizması', 'Sistemi', 
    'Aparatı', 'Elemanı', 'Takımı', 'Düzeneği', 'Modülü', 'Bileşeni', 'Parçası'
  ];
  
  // Function to generate a random location in format A-12_1
  const generateRandomLocation = (usedLocations) => {
    const columns = 'ABCDEFGHIJKLMN';
    const columnIndex = Math.floor(Math.random() * columns.length);
    const column = columns[columnIndex];
    
    let location;
    let attempts = 0;
    
    do {
      // Generate random row (01-99)
      const row = String(Math.floor(Math.random() * 99) + 1).padStart(2, '0');
      
      // Generate random shelf (1-3)
      const shelf = Math.floor(Math.random() * 3) + 1;
      
      location = `${column}-${row}_${shelf}`;
      attempts++;
      
      // Prevent infinite loop
      if (attempts > 1000) {
        throw new Error('Could not generate unique location after 1000 attempts');
      }
    } while (usedLocations.has(location));
    
    return location;
  };
  
  // Function to generate a random unique product name
  const generateRandomProductName = (usedNames) => {
    let name;
    let attempts = 0;
    
    do {
      const prefix = productPrefixes[Math.floor(Math.random() * productPrefixes.length)];
      const type = productTypes[Math.floor(Math.random() * productTypes.length)];
      const useSuffix = Math.random() > 0.5;
      const suffix = useSuffix ? ' ' + productSuffixes[Math.floor(Math.random() * productSuffixes.length)] : '';
      const size = Math.random() > 0.7 ? ` ${Math.floor(Math.random() * 100) + 1}mm` : '';
      
      name = `${prefix} ${type}${suffix}${size}`;
      attempts++;
      
      // Prevent infinite loop
      if (attempts > 1000) {
        throw new Error('Could not generate unique name after 1000 attempts');
      }
    } while (usedNames.has(name));
    
    return name;
  };
  
  // Function to handle bulk product creation
  const handleBulkCreate = async () => {
    if (isBulkCreating) return;
    
    setIsBulkCreating(true);
    setShowBulkProgress(true);
    setBulkProgress(0);
    setCreatedCount(0);
    setError(null);
    setBulkSuccess(false);
    
    try {
      const batchSize = 10; // Process in batches to prevent browser from hanging
      const totalProducts = 300;
      const usedNames = new Set();
      const usedLocations = new Set();
      
      // Use userProfile from Sidebar for creator name
      let creatorName = 'Belirtilmemiş Kullanıcı';
      if (sidebarUserProfile) {
        creatorName = `${sidebarUserProfile.first_name} ${sidebarUserProfile.last_name}`;
      }
      
      for (let i = 0; i < totalProducts; i += batchSize) {
        const batch = [];
        
        // Generate batch of products
        for (let j = 0; j < batchSize && i + j < totalProducts; j++) {
          const productName = generateRandomProductName(usedNames);
          usedNames.add(productName);
          
          const productLocation = generateRandomLocation(usedLocations);
          usedLocations.add(productLocation);
          
          // Random stock between 1 and 100
          const stock = Math.floor(Math.random() * 100) + 1;
          
          batch.push({
            name: productName,
            description: `Otomatik oluşturulmuş ${productName} ürünü.`,
            stock: stock,
            location: productLocation,
            created_at: new Date().toISOString(),
            created_by: creatorName,
            stock_history: [
              {
                type: 'create',
                amount: stock,
                previous_stock: 0,
                new_stock: stock,
                updated_at: new Date().toISOString(),
                updated_by: creatorName,
                note: 'Otomatik oluşturma'
              }
            ]
          });
        }
        
        // Insert batch into database
        const { error: batchError } = await supabase
          .from('products')
          .insert(batch);
        
        if (batchError) throw batchError;
        
        // Update progress
        const newCount = Math.min(i + batchSize, totalProducts);
        setCreatedCount(newCount);
        setBulkProgress(Math.floor((newCount / totalProducts) * 100));
        
        // Small delay to update UI
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Set final states
      setBulkProgress(100);
      setCreatedCount(totalProducts);
      setBulkSuccess(true);
      
      // Keep progress visible for a moment
      setTimeout(() => {
        setIsBulkCreating(false);
      }, 2000);
      
    } catch (err) {
      console.error('Error in bulk creation:', err);
      setError('Toplu ürün oluşturma sırasında bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
      setIsBulkCreating(false);
    }
  };

  // Kullanıcı profilini, rolünü ve izinlerini çek
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await userProfilesSupabase.auth.getUser();
      if (user) {
        const { data: userProfile } = await userProfilesSupabase
          .from('user_profiles')
          .select('*, first_name, last_name, role, stok_add_permission, stok_edit_permission, stok_movement_permission, stok_delete_permission')
          .eq('id', user.id)
          .single();
        
        if (userProfile) {
          setCurrentUser(userProfile);
          setUserRole(userProfile?.role);
          
          // Set permissions based on user profile
          setPermissions({
            stok_add_permission: Boolean(userProfile.stok_add_permission),
            stok_edit_permission: Boolean(userProfile.stok_edit_permission),
            stok_movement_permission: Boolean(userProfile.stok_movement_permission),
            stok_delete_permission: Boolean(userProfile.stok_delete_permission)
          });
        }
      }
    };
    getCurrentUser();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar 
        isOpen={isSidebarOpen} 
        onToggle={toggleSidebar} 
        customMenuItems={stockMenuItems}
        isStockPanel={true}
        onUserProfileUpdate={handleUserProfile}
      />
      
      {/* Main Content */}
      <div className={`lg:pl-72 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:pl-72' : 'lg:pl-0'}`}>
        <Navbar onToggleSidebar={toggleSidebar} />
        {/* Yetki Rozeti - moved below Navbar */}
        <div className="flex justify-end items-center p-4">
          {userRole && (
            <div className="flex items-center gap-2">
              {/* Permission status badge */}
              <div className={`flex items-center gap-2 mr-3 px-3 py-1.5 rounded-full ${
                permissions.stok_add_permission 
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800/50'
                  : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800/50'
              }`}>
                {permissions.stok_add_permission 
                  ? <CheckCircle2 className="w-4 h-4" />
                  : <XCircle className="w-4 h-4" />
                }
                <span className="text-sm font-medium">
                  {permissions.stok_add_permission 
                    ? 'Ürün Ekleme Yetkiniz Var'
                    : 'Ürün Ekleme Yetkiniz Yok'
                  }
                </span>
              </div>
              
              {/* Role badge */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                userRole === 'super'
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  : userRole === 'admin'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {userRole === 'super' ? (
                  <Shield className="w-4 h-4" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">
                  {userRole === 'super'
                    ? 'Süper Yönetici'
                    : userRole === 'admin'
                      ? 'Yönetici'
                      : 'Standart Kullanıcı'}
                </span>
              </div>
            </div>
          )}
        </div>
        <motion.main
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="py-6 px-4 sm:px-6 lg:px-8"
        >
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <button 
                  onClick={() => navigate('/stokflow')}
                  className="mr-4 p-2 rounded-lg bg-white dark:bg-gray-800 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Yeni Ürün Ekle</h1>
              </div>
              <div className="flex gap-3">
                {/* Bulk Creation Button */}
                <button
                  onClick={handleBulkCreate}
                  disabled={isBulkCreating || loading}
                  className={`px-4 py-2 ${isBulkCreating ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'} text-white rounded-lg transition-colors flex items-center gap-2 hidden`}
                >
                  {isBulkCreating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Ürünler Oluşturuluyor...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Rastgele 300 Ürün Ekle</span>
                    </>
                  )}
                </button>
                
                <button 
                  onClick={() => navigate('/stokflow/products')} 
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                  disabled={loading || isBulkCreating}
                >
                  <X className="w-4 h-4" />
                  <span>İptal</span>
                </button>
                <button 
                  onClick={handleSubmit}
                  className={`px-4 py-2 ${!permissions.stok_add_permission ? 'bg-gray-400 cursor-not-allowed' : (loading || isBulkCreating ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700')} text-white rounded-lg transition-colors flex items-center gap-2`}
                  disabled={!permissions.stok_add_permission || loading || isBulkCreating}
                  title={!permissions.stok_add_permission ? 'Ürün ekleme yetkiniz bulunmuyor' : ''}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Kaydet</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Eğer yetki yoksa uyarı göster */}
            {!permissions.stok_add_permission && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl shadow-sm flex items-start">
                <AlertCircle className="w-6 h-6 text-red-500 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-medium text-red-800 dark:text-red-300 mb-1">
                    Yetkiniz Bulunmuyor
                  </h3>
                  <p className="text-red-700 dark:text-red-400">
                    Ürün ekleme yetkiniz bulunmamaktadır. Bu işlem için gerekli izinlere sahip değilsiniz. 
                    Yetki talep etmek için sistem yöneticinizle iletişime geçiniz.
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-red-500 dark:text-red-400" />
                    <span className="text-red-700 dark:text-red-400">
                      Kullanıcı: {currentUser?.first_name} {currentUser?.last_name}
                    </span>
                    <span className="mx-2 text-red-300 dark:text-red-700">|</span>
                    <Users className="w-4 h-4 text-red-500 dark:text-red-400" />
                    <span className="text-red-700 dark:text-red-400">
                      Rol: {userRole === 'super' ? 'Süper Yönetici' : userRole === 'admin' ? 'Yönetici' : 'Standart Kullanıcı'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Bulk Creation Progress Bar */}
            {showBulkProgress && (
              <div className="mb-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-2" />
                    <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300">
                      Ürünler Oluşturuluyor ({createdCount}/300)
                    </h3>
                  </div>
                  {!isBulkCreating && (
                    <button
                      onClick={() => setShowBulkProgress(false)}
                      className="p-1 hover:bg-purple-100 dark:hover:bg-purple-800 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                    </button>
                  )}
                </div>
                
                <div className="w-full bg-purple-200 dark:bg-purple-800/50 rounded-full h-2.5 mb-1">
                  <div 
                    className="bg-purple-600 dark:bg-purple-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${bulkProgress}%` }}
                  ></div>
                </div>
                
                <p className="text-xs text-purple-600 dark:text-purple-400 text-right">
                  {bulkProgress}%
                </p>
              </div>
            )}

            {/* Status Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-md flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="mb-4 relative">
                <div className="p-3 bg-green-100 border border-green-200 text-green-700 rounded-md">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                <Save className="w-5 h-5 mr-2" />
                <span>Ürün başarıyla kaydedildi! Yönlendiriliyorsunuz...</span>
                    </div>
                    <button
                      onClick={cancelRedirect}
                      className="p-1 hover:bg-green-200 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-2 h-1 bg-green-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 transition-all duration-50 ease-linear"
                      style={{ width: `${redirectProgress}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Bulk Creation Success Notification */}
            {bulkSuccess && (
              <div className="mb-4 p-3 bg-purple-100 border border-purple-200 text-purple-700 rounded-md flex items-center justify-between">
                <div className="flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                  <span className="font-medium">Başarılı! 300 adet rastgele ürün oluşturuldu.</span>
                </div>
                <button
                  onClick={() => setBulkSuccess(false)}
                  className="p-1 hover:bg-purple-200 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-purple-600" />
                </button>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              {/* Information Box */}
              <div className="mb-6 border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden">
                <button 
                  type="button"
                  onClick={() => setInfoBoxOpen(!infoBoxOpen)}
                  className="w-full p-4 bg-blue-50 dark:bg-blue-900/20 text-left flex items-center justify-between"
                >
                  <h3 className="text-lg font-medium text-blue-800 dark:text-blue-300 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-blue-500 dark:text-blue-400" />
                    Ürün Ekleme Bilgilendirmesi
                  </h3>
                  <div className={`transform transition-transform duration-200 ${infoBoxOpen ? 'rotate-180' : ''}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                
                {infoBoxOpen && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-t border-blue-200 dark:border-blue-800">
  <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 ml-7 list-disc">
    <li>Ürün adı ve dolap konumu benzersiz olmalıdır, mevcut kayıtlarla aynı olamaz.</li>
    <li>Dolap konumu <span className="font-mono bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded">A-12_1</span> formatında girilmelidir.</li>
    <li>
      Format açıklaması: 
      <br />
      <span className="ml-4 block">• <strong>Harf</strong> (Sütun): Dolap sütununu belirtir (örneğin <code className="font-mono bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded">A, B</code>)</span>
      <span className="ml-4 block">• <strong>Sayı</strong> (Sıra): İki basamaklı rakam ile belirtilir (örneğin <code className="font-mono bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded">12</code>)</span>
      <span className="ml-4 block">• <strong>Göz</strong> (Alt çizgi sonrası): 1 ile 3 arasında bir rakam (örneğin <code className="font-mono bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded">1, 2</code>)</span>
    </li>
    <li>Stok miktarı her zaman 0 veya daha yüksek bir değer olmalıdır.</li>
    <li>Ürün görseli dosya olarak yüklenebilir veya URL olarak eklenebilir.</li>
  </ul>
</div>
                )}
              </div>

              {/* Formun tamamını stok_add_permission yoksa devre dışı bırak */}
              <fieldset disabled={!permissions.stok_add_permission} style={{ opacity: !permissions.stok_add_permission ? 0.6 : 1 }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white border-b pb-2 dark:border-gray-700">Temel Bilgiler</h3>
                    
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ürün Adı *</label>
                        <input
                          type="text"
                          name="name"
                          value={productData.name}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10"
                          placeholder="Ürün adını girin"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ürün Açıklaması</label>
                        <textarea
                          name="description"
                          value={productData.description}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[150px] max-h-[300px]"
                          placeholder="Ürün açıklamasını girin (Zorunlu değil)"
                          style={{ resize: 'vertical' }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white border-b pb-2 dark:border-gray-700">Stok ve Konum Bilgileri</h3>
                    
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mevcut Stok *</label>
                        <input
                          type="number"
                          name="stock"
                          value={productData.stock}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10"
                          placeholder="0"
                          min="0"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dolap Konumu *</label>
                        <div className="relative">
                          <input
                            type="text"
                            name="location"
                            value={productData.location}
                            onChange={handleLocationChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10"
                            placeholder="Örn: A-12_1"
                            required
                            maxLength={6}
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {productData.location ? (
                                <span className={`${productData.location.length === 6 ? 'text-green-500 dark:text-green-400' : ''}`}>
                                  {productData.location.length}/6
                                </span>
                              ) : 'A-00_1'}
                            </span>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Format: <span className="font-mono">Harf-Rakam(2)_Rakam(1-3)</span>
                          {productData.location && productData.location.length < 6 && (
                            <span className="ml-2 text-amber-500 dark:text-amber-400">
                              {productData.location.length === 0 && "Harf girişi yapınız"}
                              {productData.location.length === 2 && "Rakam girişi yapınız"}
                              {productData.location.length === 3 && "Bir rakam daha giriniz"}
                              {productData.location.length === 5 && "1, 2 veya 3 giriniz"}
                            </span>
                          )}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Satın Alma Linki</label>
                        <div className="flex items-center relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <LinkIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          </div>
                          <input
                            type="url"
                            name="purchaseLink"
                            value={productData.purchaseLink}
                            onChange={handleChange}
                            className="w-full pl-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10"
                            placeholder="https://example.com/product"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Ürün Görseli */}
                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white border-b pb-2 mb-4 dark:border-gray-700">Ürün Görseli</h3>
                  
                  <div className="flex flex-col items-center space-y-4">
                    {/* Image Source Toggle */}
                    <div className="flex justify-center space-x-4 mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={imageSource === 'file'}
                          onChange={() => setImageSource('file')}
                          className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Dosya Yükle</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={imageSource === 'url'}
                          onChange={() => setImageSource('url')}
                          className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">URL ile Ekle</span>
                      </label>
                    </div>
                    
                    {/* File Upload */}
                    {imageSource === 'file' && (
                      <div 
                        className={`w-64 h-64 border-2 border-dashed ${dragActive ? 'border-green-500 dark:border-green-600' : 'border-gray-300 dark:border-gray-600'} rounded-lg flex flex-col items-center justify-center p-4 hover:border-green-500 dark:hover:border-green-600 transition-colors cursor-pointer`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={handleDropzoneClick}
                      >
                        {productData.image ? (
                          <img 
                            src={URL.createObjectURL(productData.image)} 
                            alt="Ürün önizleme" 
                            className="max-h-full max-w-full object-contain" 
                          />
                        ) : (
                          <>
                            <Upload className="w-12 h-12 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                              Görsel yüklemek için tıklayın veya sürükleyin
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                              PNG, JPG, GIF (Max. 2MB)
                            </p>
                          </>
                        )}
                        <input 
                          ref={fileInputRef}
                          type="file" 
                          onChange={handleImageChange} 
                          className="hidden" 
                          id="product-image" 
                          accept="image/*" 
                        />
                      </div>
                    )}

                    {/* URL Input */}
                    {imageSource === 'url' && (
                      <div className="w-full max-w-md space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Görsel URL'si</label>
                          <input
                            type="url"
                            name="imageUrl"
                            value={productData.imageUrl}
                            onChange={handleImageUrlChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="https://example.com/image.jpg"
                          />
                        </div>
                        
                        {productData.imageUrl && (
                          <div className="w-64 h-64 mx-auto border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center p-2 overflow-hidden">
                            <img 
                              src={productData.imageUrl}
                              alt="Ürün önizleme" 
                              className="max-h-full max-w-full object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/400x400?text=Görsel+Yüklenemedi";
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </fieldset>
              {/* Submit Button - Hidden because we're using the header buttons */}
              <button type="submit" className="hidden">Kaydet</button>
            </form>
          </div>
        </motion.main>
      </div>
    </div>
  );
};

export default StokProductCreate; 